// Vitals colour coding (nursing-ops-2 #11): one hardcoded standard-ADULT
// threshold map shared by every vitals renderer in the nurse dashboard AND the
// IPD doctor console. Three states: null (normal — keep the default look),
// "amber", "red". Deliberately not configurable and adult-only (paediatric
// ranges are an accepted gap); if thresholds ever move server-side, this map
// is the seed data. Temperature is classified in °F, matching the recorded unit.

// Canonical keys: bp | hr | temp | spo2 | rr
const RANGES = {
  hr: { redLow: 40, amberLow: 50, amberHigh: 100, redHigh: 130 },
  temp: { redLow: 95.0, amberLow: 96.8, amberHigh: 100.3, redHigh: 102.9 },
  spo2: { redLow: 90, amberLow: 95, amberHigh: Infinity, redHigh: Infinity },
  rr: { redLow: 8, amberLow: 12, amberHigh: 20, redHigh: 30 },
  bpSys: { redLow: 90, amberLow: 100, amberHigh: 139, redHigh: 180 },
  bpDia: { redLow: 50, amberLow: 60, amberHigh: 89, redHigh: 110 },
};

const classifyNumber = (n, r) => {
  if (n < r.redLow || n > r.redHigh) return "red";
  if (n < r.amberLow || n > r.amberHigh) return "amber";
  return null;
};

const WORST = { red: 2, amber: 1, null: 0 };

// classifyVital("hr", 135) -> "red" | "amber" | null.
// "bp" accepts "120/80" (classifies the worse of sys/dia). Unparseable or
// missing values return null — never colour what we can't read.
export function classifyVital(key, value) {
  if (value == null || value === "") return null;
  if (key === "bp") {
    const m = String(value).match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const sys = classifyNumber(parseFloat(m[1]), RANGES.bpSys);
    const dia = classifyNumber(parseFloat(m[2]), RANGES.bpDia);
    return WORST[sys] >= WORST[dia] ? sys : dia;
  }
  const r = RANGES[key];
  if (!r) return null;
  const n = parseFloat(String(value).replace(/[^\d.-]/g, ""));
  return Number.isNaN(n) ? null : classifyNumber(n, r);
}

// Which side of the range a value fell off, for the H/L flag. Returns
// "high" | "low" | null. BP reports the direction of whichever of sys/dia was
// the worse of the two, so the flag agrees with the colour.
function vitalDirection(key, value) {
  if (value == null || value === "") return null;
  const sideOf = (n, r) => {
    if (Number.isNaN(n)) return null;
    if (n < r.amberLow) return "low";
    if (n > r.amberHigh) return "high";
    return null;
  };
  if (key === "bp") {
    const m = String(value).match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const sys = classifyNumber(parseFloat(m[1]), RANGES.bpSys);
    const dia = classifyNumber(parseFloat(m[2]), RANGES.bpDia);
    return WORST[sys] >= WORST[dia]
      ? sideOf(parseFloat(m[1]), RANGES.bpSys)
      : sideOf(parseFloat(m[2]), RANGES.bpDia);
  }
  const r = RANGES[key];
  if (!r) return null;
  return sideOf(parseFloat(String(value).replace(/[^\d.-]/g, "")), r);
}

// Text colours for plain-text renderers (tables, panel values). Both are
// measured against the white card they render on: red 4.98:1, amber 5.02:1.
// Amber was #ed6c02 (3.11:1), which failed AA at the 13-14px these render at —
// and amber is the borderline band, the one most likely to be skimmed past.
export const VITAL_STATE_COLORS = { red: "#d32f2f", amber: "#B45309" };

// sx fragment for a value's Typography/TableCell — {} when normal.
//
// The H/L suffix is the point of this, not the colour. Colour alone fails
// WCAG 1.4.1 and, more to the point, fails a colour-blind nurse on a
// glare-washed ward tablet; the `fontWeight` that used to be the second signal
// was never one, because every caller already sets 700 unconditionally. The
// flag follows lab-report convention (H/L), rides in ::after so all four
// callers get it without changing a line, and is spaced so it reads as a flag
// rather than a digit.
export const vitalValueSx = (key, value) => {
  const state = classifyVital(key, value);
  if (!state) return {};
  const direction = vitalDirection(key, value);
  return {
    color: VITAL_STATE_COLORS[state],
    fontWeight: 700,
    ...(direction && {
      "&::after": {
        content: `"${direction === "high" ? "H" : "L"}"`,
        marginLeft: "0.3em",
        fontSize: "0.85em",
        fontWeight: 700,
        verticalAlign: "super",
      },
    }),
  };
};

// MUI Chip color prop for chip renderers — "default" when normal.
export const vitalChipColor = (key, value) => {
  const state = classifyVital(key, value);
  return state === "red" ? "error" : state === "amber" ? "warning" : "default";
};
